import { useEffect, useMemo, useState } from "react";
import { api, brl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Users, DollarSign, Trees, Pencil, Image as ImageIcon, Star, StarOff, Upload, EyeOff, Eye, MoveUp, MoveDown } from "lucide-react";
import { toast } from "sonner";

// Tipos com capacidade padrão sugerida (usuário pode ajustar)
const TIPOS_INFO = [
  { label: "Quarto Individual", cap: 1, key: "Quarto Individual" },
  { label: "Quarto de Casal", cap: 2, key: "Quarto de Casal" },
  { label: "Quarto Triplo", cap: 3, key: "Quarto Triplo" },
  { label: "Quarto Família", cap: 4, key: "Quarto Família" },
  { label: "Quarto Quádruplo/Grupo", cap: 6, key: "Quarto Quádruplo/Grupo" },
  { label: "Chácara (diária ou fim de semana)", cap: 12, key: "Chácara" },
];
const TIPOS = TIPOS_INFO.map((t) => t.key);

const STATUS_LABEL = {
  disponivel: "Disponível",
  ocupado: "Ocupado",
  manutencao: "Manutenção",
};

const PESSOAS_SLOTS = [1, 2, 3, 4, 5, 6];

const emptyPrecos = () => Object.fromEntries(PESSOAS_SLOTS.map((n) => [String(n), ""]));

// F-MOTOR-RESERVAS.2.B — catálogo de comodidades do quarto (slugs + labels PT-BR).
// Deve espelhar `COMODIDADES_QUARTO` em backend/server.py.
const COMODIDADES_QUARTO = [
  { slug: "wifi", label: "Wi-Fi" },
  { slug: "ar_condicionado", label: "Ar-condicionado" },
  { slug: "ventilador", label: "Ventilador" },
  { slug: "aquecedor", label: "Aquecedor" },
  { slug: "tv", label: "TV" },
  { slug: "tv_cabo", label: "TV a Cabo" },
  { slug: "smart_tv", label: "Smart TV" },
  { slug: "netflix", label: "Netflix" },
  { slug: "frigobar", label: "Frigobar" },
  { slug: "cofre", label: "Cofre" },
  { slug: "secador_cabelo", label: "Secador de Cabelo" },
  { slug: "banheira", label: "Banheira" },
  { slug: "box_blindex", label: "Box Blindex" },
  { slug: "cama_king", label: "Cama King" },
  { slug: "cama_queen", label: "Cama Queen" },
  { slug: "cama_solteiro", label: "Cama de Solteiro" },
  { slug: "varanda", label: "Varanda" },
  { slug: "vista_jardim", label: "Vista para o Jardim" },
  { slug: "vista_piscina", label: "Vista para a Piscina" },
  { slug: "vista_natureza", label: "Vista para a Natureza" },
  { slug: "escrivaninha", label: "Escrivaninha" },
  { slug: "fechadura_eletronica", label: "Fechadura Eletrônica" },
];

const DESCRICAO_MAX = 2000;

const EMPTY_FORM = {
  pousada_id: "", numero: "", tipo: "Quarto de Casal",
  capacidade: 2, valor_diaria: 250, status: "disponivel",
  precos_por_pessoa: emptyPrecos(),
  preco_empresa: "", preco_individual: "",
  is_chacara_inteira: false,
  descricao: "",
  comodidades: [],
};

// F-MOTOR-RESERVAS.2.D — Galeria de fotos do Quarto (mesmo padrão da 2.C).
// Mantido no mesmo arquivo (padrão do projeto). Reutiliza o endpoint público
// unificado `/api/public/fotos/{foto_id}` da 2.C para servir a imagem.
const FOTO_MIME_ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const FOTO_MAX_BYTES = 5 * 1024 * 1024;

function QuartoFotosGallery({ quartoId }) {
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quartos/${quartoId}/fotos`, {
        params: { incluir_inativas: true },
      });
      setFotos(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar fotos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (quartoId) load(); /* eslint-disable-next-line */ }, [quartoId]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    let okCount = 0;
    for (const file of files) {
      if (!FOTO_MIME_ALLOWED.includes(file.type)) {
        toast.error(`${file.name}: formato não permitido (JPG/PNG/WebP)`);
        continue;
      }
      if (file.size > FOTO_MAX_BYTES) {
        toast.error(`${file.name}: arquivo maior que 5 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/quartos/${quartoId}/fotos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        okCount += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Erro ao enviar ${file.name}`);
      }
    }
    if (okCount > 0) toast.success(`${okCount} foto(s) enviada(s)`);
    setUploading(false);
    load();
  };

  const definirPrincipal = async (foto) => {
    try {
      await api.patch(`/quartos/${quartoId}/fotos/${foto.id}`, { principal: true });
      toast.success("Foto principal atualizada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao definir principal");
    }
  };

  const toggleAtivo = async (foto) => {
    try {
      await api.patch(`/quartos/${quartoId}/fotos/${foto.id}`, { ativo: !foto.ativo });
      toast.success(foto.ativo ? "Foto desativada" : "Foto ativada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar status");
    }
  };

  const salvarLegenda = async (foto, legenda) => {
    if ((legenda || "") === (foto.legenda || "")) return;
    try {
      await api.patch(`/quartos/${quartoId}/fotos/${foto.id}`, { legenda });
      toast.success("Legenda salva");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar legenda");
    }
  };

  const excluir = async (foto) => {
    if (!window.confirm("Excluir esta foto? Esta ação é permanente.")) return;
    try {
      await api.delete(`/quartos/${quartoId}/fotos/${foto.id}`);
      toast.success("Foto removida");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover foto");
    }
  };

  const mover = async (foto, dir) => {
    const ativasOrdenadas = [...fotos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
    const idx = ativasOrdenadas.findIndex((f) => f.id === foto.id);
    const alvo = idx + dir;
    if (idx < 0 || alvo < 0 || alvo >= ativasOrdenadas.length) return;
    const nova = ativasOrdenadas.slice();
    [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
    try {
      await api.patch(`/quartos/${quartoId}/fotos/reordenar`, {
        ordem: nova.map((f) => f.id),
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reordenar");
    }
  };

  const fotosOrdenadas = [...fotos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const fotoUrl = (f) => `${API_BASE}/api/public/fotos/${f.id}`;

  return (
    <div data-testid={`quarto-fotos-gallery-${quartoId}`} className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] text-stone-500">
          JPG, PNG ou WebP · até 5 MB por arquivo · a primeira foto vira principal automaticamente.
        </div>
        <label
          data-testid={`btn-add-foto-quarto-${quartoId}`}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm cursor-pointer border transition-colors ${
            uploading
              ? "bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed"
              : "bg-[#083A6B] hover:bg-[#052B50] text-white border-[#083A6B]"
          }`}
        >
          <Upload size={14} />
          {uploading ? "Enviando..." : "Adicionar fotos"}
          <input
            type="file"
            data-testid={`input-upload-foto-quarto-${quartoId}`}
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            disabled={uploading}
            onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
          />
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-stone-500 py-8 text-center">Carregando...</div>
      ) : fotosOrdenadas.length === 0 ? (
        <div
          data-testid="quarto-fotos-empty"
          className="border-2 border-dashed border-stone-300 rounded-md p-8 text-center bg-stone-50"
        >
          <ImageIcon size={28} className="mx-auto text-stone-400 mb-3" />
          <div className="font-serif text-lg text-stone-700">Nenhuma foto ainda</div>
          <div className="text-sm text-stone-500 mt-1">Clique em "Adicionar fotos" para começar.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {fotosOrdenadas.map((f, idx) => (
            <div
              key={f.id}
              data-testid={`foto-quarto-${f.id}`}
              data-principal={f.principal ? "true" : "false"}
              data-ativo={f.ativo ? "true" : "false"}
              className={`relative border rounded-md overflow-hidden bg-white group ${
                f.principal ? "border-[#083A6B] ring-2 ring-[#083A6B]/30" : "border-stone-200"
              } ${!f.ativo ? "opacity-60" : ""}`}
            >
              <div className="aspect-video bg-stone-100">
                <img
                  src={fotoUrl(f)}
                  alt={f.legenda || f.nome_original || "Foto"}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              {f.principal && (
                <span
                  data-testid={`foto-quarto-badge-principal-${f.id}`}
                  className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#083A6B] text-white px-2 py-0.5 rounded-full"
                >
                  <Star size={10} /> Principal
                </span>
              )}
              {!f.ativo && (
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest bg-stone-700 text-white px-2 py-0.5 rounded-full">
                  Inativa
                </span>
              )}
              <div className="p-2 space-y-2">
                <Input
                  data-testid={`input-foto-quarto-legenda-${f.id}`}
                  defaultValue={f.legenda || ""}
                  placeholder="Legenda (opcional)"
                  className="h-8 text-xs"
                  onBlur={(e) => salvarLegenda(f, e.target.value)}
                />
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`btn-mover-up-quarto-${f.id}`}
                      disabled={idx === 0}
                      onClick={() => mover(f, -1)}
                      className="text-stone-500 hover:text-[#083A6B] p-1 disabled:opacity-30"
                      aria-label="Mover para cima"
                    >
                      <MoveUp size={14} />
                    </button>
                    <button
                      type="button"
                      data-testid={`btn-mover-down-quarto-${f.id}`}
                      disabled={idx === fotosOrdenadas.length - 1}
                      onClick={() => mover(f, 1)}
                      className="text-stone-500 hover:text-[#083A6B] p-1 disabled:opacity-30"
                      aria-label="Mover para baixo"
                    >
                      <MoveDown size={14} />
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    {!f.principal && f.ativo && (
                      <button
                        type="button"
                        data-testid={`btn-principal-quarto-${f.id}`}
                        onClick={() => definirPrincipal(f)}
                        className="text-stone-500 hover:text-[#083A6B] p-1"
                        aria-label="Definir como principal"
                        title="Definir como principal"
                      >
                        <StarOff size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`btn-toggle-ativo-quarto-${f.id}`}
                      onClick={() => toggleAtivo(f)}
                      className="text-stone-500 hover:text-[#083A6B] p-1"
                      aria-label={f.ativo ? "Desativar" : "Ativar"}
                      title={f.ativo ? "Desativar" : "Ativar"}
                    >
                      {f.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      type="button"
                      data-testid={`btn-delete-foto-quarto-${f.id}`}
                      onClick={() => excluir(f)}
                      className="text-stone-500 hover:text-[#D4A652] p-1"
                      aria-label="Excluir"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function Quartos() {
  const [quartos, setQuartos] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const comodidadesSet = useMemo(() => new Set(form.comodidades || []), [form.comodidades]);

  const load = async () => {
    try {
      const [{ data: q }, { data: p }] = await Promise.all([api.get("/quartos"), api.get("/pousadas")]);
      setQuartos(q);
      setPousadas(p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar quartos. Tente novamente.");
    }
  };

  useEffect(() => { load(); }, []);

  const pousadaName = (id) => pousadas.find((x) => x.id === id)?.nome || "—";
  const selectedPousada = pousadas.find((x) => x.id === form.pousada_id);
  const isPousadaChacara = !!selectedPousada?.is_chacara;
  const jaTemChacaraInteira = useMemo(
    () => !!quartos.find((q) => q.pousada_id === form.pousada_id && q.is_chacara_inteira && q.id !== editingId),
    [quartos, form.pousada_id, editingId],
  );

  const filtered = useMemo(() => {
    if (filter === "all") return quartos;
    return quartos.filter((q) => q.pousada_id === filter);
  }, [quartos, filter]);

  const onChangeTipo = (v) => {
    const info = TIPOS_INFO.find((t) => t.key === v);
    setForm((f) => ({ ...f, tipo: v, capacidade: info?.cap ?? f.capacidade }));
  };

  const toggleComodidade = (slug) => {
    setForm((f) => {
      const has = (f.comodidades || []).includes(slug);
      return {
        ...f,
        comodidades: has
          ? f.comodidades.filter((s) => s !== slug)
          : [...(f.comodidades || []), slug],
      };
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    const precos = emptyPrecos();
    Object.entries(q.precos_por_pessoa || {}).forEach(([k, v]) => {
      if (precos[k] !== undefined) precos[k] = String(v ?? "");
    });
    setForm({
      pousada_id: q.pousada_id || "",
      numero: q.numero || "",
      tipo: q.tipo || "Quarto de Casal",
      capacidade: q.capacidade ?? 2,
      valor_diaria: q.valor_diaria ?? 0,
      status: q.status || "disponivel",
      precos_por_pessoa: precos,
      preco_empresa: q.preco_empresa == null ? "" : String(q.preco_empresa),
      preco_individual: q.preco_individual == null ? "" : String(q.preco_individual),
      is_chacara_inteira: !!q.is_chacara_inteira,
      descricao: q.descricao || "",
      comodidades: Array.isArray(q.comodidades) ? [...q.comodidades] : [],
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pousada_id) return toast.error("Selecione a pousada");
    if (!form.numero.trim()) return toast.error("Informe o número do quarto");
    if ((form.descricao || "").length > DESCRICAO_MAX)
      return toast.error(`Descrição excede ${DESCRICAO_MAX} caracteres`);
    const precos_por_pessoa = Object.fromEntries(
      Object.entries(form.precos_por_pessoa)
        .filter(([, v]) => v !== "" && !Number.isNaN(Number(v)))
        .map(([k, v]) => [k, Number(v)])
    );
    const payload = {
      numero: form.numero,
      tipo: form.tipo,
      capacidade: Number(form.capacidade),
      valor_diaria: Number(form.valor_diaria),
      status: form.status,
      precos_por_pessoa,
      preco_empresa: form.preco_empresa === "" ? null : Number(form.preco_empresa),
      preco_individual: form.preco_individual === "" ? null : Number(form.preco_individual),
      is_chacara_inteira: !!form.is_chacara_inteira,
      descricao: form.descricao,
      comodidades: form.comodidades,
    };
    try {
      if (editingId) {
        await api.patch(`/quartos/${editingId}`, payload);
        toast.success("Quarto atualizado");
      } else {
        await api.post("/quartos", { ...payload, pousada_id: form.pousada_id });
        toast.success("Quarto cadastrado");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar quarto");
    }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/quartos/${id}`, { status }); toast.success("Status atualizado"); load(); }
    catch { toast.error("Erro ao atualizar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este quarto? Reservas relacionadas serão removidas.")) return;
    try { await api.delete(`/quartos/${id}`); toast.success("Quarto removido"); load(); }
    catch { toast.error("Erro ao remover"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Acomodações"
        title="Quartos"
        description="Cadastre quartos, defina o tipo e configure preços diferentes por número de pessoas."
        actions={
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="filter-pousada" className="w-56 bg-white">
                <SelectValue placeholder="Filtrar pousada" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as pousadas</SelectItem>
                {pousadas.filter((p) => p.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY_FORM); } }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-novo-quarto" onClick={openCreate} className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                  <Plus size={16} className="mr-2" /> Novo quarto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">
                    {editingId ? "Editar quarto" : "Novo quarto"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="form-quarto">
                  <Tabs defaultValue="dados" className="w-full">
                    <TabsList className="grid grid-cols-4 w-full">
                      <TabsTrigger value="dados" data-testid="tab-quarto-dados">Dados</TabsTrigger>
                      <TabsTrigger value="descricao" data-testid="tab-quarto-descricao">Descrição</TabsTrigger>
                      <TabsTrigger value="comodidades" data-testid="tab-quarto-comodidades">Comodidades</TabsTrigger>
                      <TabsTrigger value="fotos" data-testid="tab-quarto-fotos">Fotos</TabsTrigger>
                    </TabsList>

                    {/* -------- Dados -------- */}
                    <TabsContent value="dados" className="space-y-4 pt-4">
                      <div>
                        <Label>Pousada</Label>
                        <Select
                          value={form.pousada_id}
                          onValueChange={(v) => setForm({ ...form, pousada_id: v, is_chacara_inteira: false })}
                          disabled={!!editingId}
                        >
                          <SelectTrigger data-testid="select-quarto-pousada"><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {pousadas.filter((p) => p.id).map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.nome}{p.is_chacara ? " · Chácara" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {isPousadaChacara && (
                        <div
                          data-testid="chacara-inteira-block"
                          className={`flex items-center justify-between border rounded-md p-3 ${form.is_chacara_inteira ? "border-[#083A6B] bg-[#EAF0EA]" : "border-stone-200 bg-stone-50"}`}
                        >
                          <div>
                            <div className="text-sm font-medium text-stone-800 flex items-center gap-2">
                              <Trees size={14} className="text-[#083A6B]" /> Este quarto representa a chácara inteira?
                            </div>
                            <div className="text-[11px] text-stone-500 mt-0.5">
                              Marque para criar o "quarto-container" (uma reserva desta unidade bloqueia todos os quartos individuais).
                              {jaTemChacaraInteira && !form.is_chacara_inteira && " Já existe uma chácara inteira cadastrada nesta pousada."}
                            </div>
                          </div>
                          <Switch
                            data-testid="switch-chacara-inteira"
                            checked={form.is_chacara_inteira}
                            disabled={jaTemChacaraInteira}
                            onCheckedChange={(v) => setForm((f) => ({
                              ...f,
                              is_chacara_inteira: v,
                              tipo: v ? "Chácara" : f.tipo,
                              numero: v ? (f.numero || "Chácara Inteira") : f.numero,
                              capacidade: v ? Math.max(Number(f.capacidade || 0), 12) : f.capacidade,
                            }))}
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label>Número</Label>
                          <Input data-testid="input-quarto-numero" value={form.numero}
                            onChange={(e) => setForm({ ...form, numero: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                          <Label>Tipo</Label>
                          <Select value={form.tipo} onValueChange={onChangeTipo}>
                            <SelectTrigger data-testid="select-quarto-tipo"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TIPOS_INFO.map((t) => (
                                <SelectItem key={t.key} value={t.key}>
                                  {t.label} <span className="text-stone-400 text-xs ml-1">· cap. {t.cap}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Capacidade</Label>
                          <Input type="number" min="1" data-testid="input-quarto-capacidade" value={form.capacidade}
                            onChange={(e) => setForm({ ...form, capacidade: e.target.value })} />
                        </div>
                        <div>
                          <Label>Valor padrão / diária (R$)</Label>
                          <Input type="number" min="0" step="0.01" data-testid="input-quarto-valor" value={form.valor_diaria}
                            onChange={(e) => setForm({ ...form, valor_diaria: e.target.value })} />
                        </div>
                      </div>

                      <div className="bg-stone-50 border border-stone-200 rounded-md p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Users size={14} className="text-[#D4A652]" />
                          <div className="text-xs uppercase tracking-widest text-stone-600 font-semibold">
                            Preços por número de pessoas
                          </div>
                        </div>
                        <p className="text-xs text-stone-500 mb-3">
                          Deixe em branco para usar o valor padrão. O cliente escolhe quantas pessoas na reserva e o sistema aplica o preço automaticamente.
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {PESSOAS_SLOTS.map((n) => (
                            <div key={n}>
                              <Label className="text-xs">
                                {n} {n === 1 ? "pessoa" : "pessoas"}
                              </Label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">R$</span>
                                <Input
                                  type="number" min="0" step="0.01"
                                  data-testid={`input-preco-${n}`}
                                  className="pl-9"
                                  value={form.precos_por_pessoa[String(n)]}
                                  onChange={(e) => setForm({
                                    ...form,
                                    precos_por_pessoa: { ...form.precos_por_pessoa, [String(n)]: e.target.value },
                                  })}
                                  placeholder="—"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Tarifa Empresa (R$)</Label>
                          <Input
                            type="number" min="0" step="0.01"
                            data-testid="input-preco-empresa"
                            value={form.preco_empresa}
                            onChange={(e) => setForm({ ...form, preco_empresa: e.target.value })}
                            placeholder="Opcional"
                          />
                          <p className="text-[10px] text-stone-500 mt-1">Preço/pacote para reservas via empresa contratante.</p>
                        </div>
                        <div>
                          <Label>Tarifa Individual (R$)</Label>
                          <Input
                            type="number" min="0" step="0.01"
                            data-testid="input-preco-individual"
                            value={form.preco_individual}
                            onChange={(e) => setForm({ ...form, preco_individual: e.target.value })}
                            placeholder="Opcional"
                          />
                          <p className="text-[10px] text-stone-500 mt-1">Ex.: Pousada Central de Cabreúva usa R$ 129 para hóspede individual.</p>
                        </div>
                      </div>
                    </TabsContent>

                    {/* -------- Descrição -------- */}
                    <TabsContent value="descricao" className="space-y-4 pt-4">
                      <div>
                        <div className="flex items-center justify-between">
                          <Label htmlFor="descricao">Descrição</Label>
                          <span data-testid="count-quarto-descricao" className="text-[11px] text-stone-500">
                            {(form.descricao || "").length}/{DESCRICAO_MAX}
                          </span>
                        </div>
                        <Textarea
                          id="descricao"
                          data-testid="input-quarto-descricao"
                          value={form.descricao}
                          maxLength={DESCRICAO_MAX}
                          onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                          placeholder="Descrição completa do quarto (texto simples)."
                          rows={10}
                        />
                      </div>
                    </TabsContent>

                    {/* -------- Comodidades -------- */}
                    <TabsContent value="comodidades" className="space-y-3 pt-4">
                      <div className="text-[12px] text-stone-500">
                        Selecione as comodidades presentes no quarto. Elas aparecerão nas páginas do site.
                      </div>
                      <div className="flex flex-wrap gap-2" data-testid="list-quarto-comodidades">
                        {COMODIDADES_QUARTO.map((c) => {
                          const on = comodidadesSet.has(c.slug);
                          return (
                            <button
                              key={c.slug}
                              type="button"
                              data-testid={`chip-comodidade-quarto-${c.slug}`}
                              data-selected={on ? "true" : "false"}
                              onClick={() => toggleComodidade(c.slug)}
                              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                on
                                  ? "bg-[#083A6B] text-white border-[#083A6B]"
                                  : "bg-white text-stone-700 border-stone-300 hover:border-[#083A6B]"
                              }`}
                            >
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </TabsContent>

                    {/* -------- Fotos (F-MOTOR-RESERVAS.2.D) -------- */}
                    <TabsContent value="fotos" className="pt-4">
                      {editingId ? (
                        <QuartoFotosGallery quartoId={editingId} />
                      ) : (
                        <div
                          data-testid="placeholder-quarto-fotos"
                          className="border-2 border-dashed border-stone-300 rounded-md p-8 text-center bg-stone-50"
                        >
                          <ImageIcon size={28} className="mx-auto text-stone-400 mb-3" />
                          <div className="font-serif text-lg text-stone-700">Salve o quarto primeiro</div>
                          <div className="text-sm text-stone-500 mt-1">
                            O gerenciamento de fotos é habilitado após o cadastro inicial.
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>

                  <DialogFooter>
                    <Button type="submit" data-testid="btn-submit-quarto" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                      {editingId ? "Salvar alterações" : "Cadastrar"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nº</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Cap.</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Preços por pessoa</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Padrão</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tarifas</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((q) => (
              <TableRow key={q.id} data-testid={`quarto-row-${q.id}`}>
                <TableCell className="font-medium text-stone-800">{pousadaName(q.pousada_id)}</TableCell>
                <TableCell className="font-serif text-lg">
                  {q.is_chacara_inteira ? (
                    <span data-testid={`chacara-inteira-tag-${q.id}`} className="inline-flex items-center gap-1 text-[#083A6B]">
                      <Trees size={14} /> Chácara Inteira
                    </span>
                  ) : q.numero}
                </TableCell>
                <TableCell>{q.tipo}</TableCell>
                <TableCell>{q.capacidade}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {PESSOAS_SLOTS.map((n) => {
                      const v = q.precos_por_pessoa?.[String(n)];
                      if (!v) return null;
                      return (
                        <span key={n} className="inline-flex items-center gap-1 text-[11px] bg-[#EAF0EA] text-[#083A6B] px-2 py-0.5 rounded-full">
                          <DollarSign size={10} /> {n}p · {brl(v)}
                        </span>
                      );
                    })}
                    {!q.precos_por_pessoa || Object.values(q.precos_por_pessoa || {}).filter(Boolean).length === 0 ? (
                      <span className="text-xs text-stone-400">—</span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="font-medium">{brl(q.valor_diaria)}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 min-w-[110px]">
                    {q.preco_empresa != null && (
                      <span data-testid={`preco-empresa-${q.id}`} className="text-[10px] uppercase tracking-widest bg-[#EAF0EA] text-[#083A6B] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        Empresa · {brl(q.preco_empresa)}
                      </span>
                    )}
                    {q.preco_individual != null && (
                      <span data-testid={`preco-individual-${q.id}`} className="text-[10px] uppercase tracking-widest bg-[#FDF3E3] text-[#8B6A1F] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        Individual · {brl(q.preco_individual)}
                      </span>
                    )}
                    {q.preco_empresa == null && q.preco_individual == null && (
                      <span className="text-xs text-stone-400">—</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Select value={q.status} onValueChange={(v) => changeStatus(q.id, v)}>
                    <SelectTrigger data-testid={`select-status-${q.id}`} className="w-36 h-8 bg-transparent border-stone-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABEL).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      data-testid={`btn-edit-quarto-${q.id}`}
                      onClick={() => openEdit(q)}
                      className="text-stone-400 hover:text-[#083A6B] p-1"
                      aria-label="Editar"
                    >
                      <Pencil size={16} />
                    </button>
                    <button data-testid={`btn-delete-quarto-${q.id}`} onClick={() => remove(q.id)}
                      className="text-stone-400 hover:text-[#D4A652] p-1">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-stone-500">
                  Nenhum quarto cadastrado. Clique em Novo quarto para começar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
